import { emitToasterMsg } from './toasterUtility';
import axiosHttp from './interceptor';

const POLL_INTERVAL = 5000;
const STORAGE_KEY = "webhook_queue_operations";

class WebhookQueueService {
  constructor() {
    this.queue = [];
    this.completedOps = [];
    this.listeners = [];
    this.pollingTimer = null;
    this._loadFromStorage();
    this._resumePollingIfNeeded();  
  }

  // Subscribe to queue state changes
  subscribe(listener) {
    this.listeners.push(listener);
    return () => {
      this.listeners = this.listeners.filter((l) => l !== listener);
    };
  }

  _notify() {
    const state = this.getState();
    this.listeners.forEach((listener) => listener(state));
    this._saveToStorage();
  }

  _loadFromStorage() {
    try {
      const stored = sessionStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        this.queue = (parsed.queue || []).map((op) => ({
          ...op,
          onComplete: null,
          onError: null,
          pollStatusFn: null,
        }));
        this.completedOps = parsed.completedOps || [];
      }
    } catch {
    }
  }

  /**
   * After restoring from storage, resume polling for any operations
   * that were still processing before the page refreshed.
   */
  _resumePollingIfNeeded() {
    const MAX_AGE_MS = 30 * 60 * 1000;
    const now = Date.now();

    // Expire stale operations
    this.queue = this.queue.filter((op) => {
      if (
        (op.status === "processing" || op.status === "pending") &&
        now - new Date(op.updatedAt).getTime() > MAX_AGE_MS
      ) {
        op.status = "failed";
        op.error = "Operation timed out (stale after page refresh).";
        this.completedOps.unshift(op);
        return false;
      }
      return true;
    });

    const hasProcessing = this.queue.some(
      (op) => op.status === "processing" && op.webhookId
    );
    if (hasProcessing) {
      this._startPolling();
    }
  }

  _saveToStorage() {
    try {
      sessionStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          queue: this.queue,
          completedOps: this.completedOps,
        })
      );
    } catch {
    }
  }

  getState() {
    return {
      queue: [...this.queue],
      completedOps: [...this.completedOps],
      hasPending: this.queue.some(
        (op) => op.status === "pending" || op.status === "processing"
      ),
      waitingForInput: this.queue.filter((op) => op.status === "waitingforinput"),
    };
  }

  /**
   * Enqueue a new operation to be processed by the backend webhook system.
   * @param {Function} apiCall - A function that returns a Promise (the API call).
   * @param {Object} options
   * @param {string} options.label - Display name for the operation.
   * @param {string} [options.description] - Optional description.
   * @param {Function} [options.onComplete] - Callback when operation completes.
   * @param {Function} [options.onError] - Callback when operation fails.
   * @param {Function} [options.pollStatusFn] - Custom polling function.
   * @returns {string} operationId
   */
  async enqueue(apiCall, options = {}) {
    const operationId = `op_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    const operation = {
      id: operationId,
      label: options.label || "Queued Operation",
      description: options.description || "",
      status: "pending", // pending | processing | completed | failed
      progress: 0,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      result: null,
      error: null,
      webhookId: null,
      onComplete: options.onComplete || null,
      onError: options.onError || null,
      pollStatusFn: options.pollStatusFn || null,
      silent: options.silent || false,
    };

    this.queue.push(operation);
    this._notify();

    if (!operation.silent) {
      emitToasterMsg('success', 'Operation Queued', `"${operation.label}" has been queued. You may continue working while it processes in the background.`);
    }

    try {
      const response = await apiCall();
      const data = response?.data;

      // Extract webhookId, check both top level and nested "data" property
      const webhookId =
        data?.webhookId ||
        data?.data?.webhookId ||
        data?.Data?.webhookId ||
        data?.jobId ||
        data?.data?.jobId ||
        data?.operationId ||
        data?.data?.operationId ||
        data?.id ||
        data?.data?.id;

      if (webhookId) {
        operation.webhookId = webhookId;
        operation.status = "processing";
        this._notify();
        this._startPolling();
      } else if (data?.isSuccess === false) {
        const errorMessage =
          data?.errorMessage ||
          data?.data?.errorMessage ||
          data?.message ||
          "Operation failed.";
        this._failOperation(operationId, errorMessage);
      } else {
        // No webhook ID, treat as immediate completion
        this._completeOperation(operationId, data);
      }
    } catch (error) {
      operation.status = "failed";
      operation.error =
        error?.response?.data?.message || error?.message || "Unknown error";
      operation.updatedAt = new Date().toISOString();
      this._notify();
      this._moveToCompleted(operationId);

      if (operation.onError) {
        operation.onError(error);
      }
    }

    return operationId;
  }

  /**
   * Poll the backend for status updates on processing operations.
   * Expects a GET endpoint: /api/webhook/status/{webhookId}
   */
  _startPolling() {
    if (this.pollingTimer) return;

    this.pollingTimer = setInterval(async () => {
      const processingOps = this.queue.filter(
        (op) => op.status === "processing" && op.webhookId
      );

      if (processingOps.length === 0) {
        clearInterval(this.pollingTimer);
        this.pollingTimer = null;
        return;
      }

      for (const op of processingOps) {
        try {
          let data;

          if (op.pollStatusFn) {
            data = await op.pollStatusFn(op.webhookId);
          } else {
            const response = await axiosHttp.get(`api/Webhook/status/${op.webhookId}`);
            data = response?.data;
          }

          const payload = data?.Data || data?.data || data;

          const progress = payload?.progress ?? payload?.Progress;
          if (progress !== undefined && progress !== null) {
            op.progress = progress;
          }

          const progressMessage = payload?.progressMessage ?? payload?.ProgressMessage;
          if (progressMessage !== undefined && progressMessage !== null) {
            op.progressMessage = progressMessage;
          }

          const status = (payload?.status || payload?.Status || "").toLowerCase();

          if (status === "completed" || status === "success") {
            const result = payload?.result || payload?.Result || payload;
            this._completeOperation(op.id, result);
          } else if (status === "failed" || status === "error") {
            const error =
              payload?.error ||
              payload?.Error ||
              payload?.message ||
              payload?.Message ||
              payload?.errorMessage ||
              payload?.ErrorMessage ||
              "Operation failed on server.";
            this._failOperation(op.id, error);
          } else if (status === "waitingforinput") {
            const result = payload?.result || payload?.Result || payload;
            op.status = "waitingforinput";
            op.prompt = result;
            op.updatedAt = new Date().toISOString();
          } else {
            op.updatedAt = new Date().toISOString();
          }

          this._notify();
        } catch {
        }
      }
    }, POLL_INTERVAL);
  }

  _completeOperation(operationId, result) {
    const op = this.queue.find((o) => o.id === operationId);
    if (!op) return;

    op.status = "completed";
    op.progress = 100;
    op.result = result;
    op.updatedAt = new Date().toISOString();

    if (!op.silent) {
      emitToasterMsg('success', 'Operation Complete', `"${op.label}" completed successfully.`);
    }

    if (op.onComplete) {
      op.onComplete(result);
    }

    this._moveToCompleted(operationId);
    this._notify();
  }

  _failOperation(operationId, error, statusData) {
    const op = this.queue.find((o) => o.id === operationId);
    if (!op) return;

    op.status = "failed";
    op.error = error;
    op.updatedAt = new Date().toISOString();
    this._notify();

    if (op.onError) {
      op.onError(error, statusData);
    }

    this._moveToCompleted(operationId);
  }

  _moveToCompleted(operationId) {
    const index = this.queue.findIndex((o) => o.id === operationId);
    if (index === -1) return;

    const [op] = this.queue.splice(index, 1);
    const serializable = { ...op };
    delete serializable.onComplete;
    delete serializable.onError;
    this.completedOps.unshift(serializable);

    if (this.completedOps.length > 50) {
      this.completedOps = this.completedOps.slice(0, 50);
    }

    this._notify();
  }

  /**
   * Manually mark an operation as completed (e.g., from a WebSocket event).
   */
  resolveOperation(webhookId, result) {
    const op = this.queue.find((o) => o.webhookId === webhookId);
    if (op) {
      this._completeOperation(op.id, result);
    }
  }

  /**
   * Manually mark an operation as failed (e.g., from a WebSocket event).
   */
  rejectOperation(webhookId, error) {
    const op = this.queue.find((o) => o.webhookId === webhookId);
    if (op) {
      this._failOperation(op.id, error);
    }
  }

  /**
   * Clear all completed/failed operations from history.
   */
  clearHistory() {
    this.completedOps = [];
    this._notify();
  }

  /**
   * Cancel a pending/processing operation.
   */
  async cancelOperation(operationId) {
    const op = this.queue.find((o) => o.id === operationId);
    if (!op) return;

    if (op.webhookId) {
      try {
        await axiosHttp.post(`api/Webhook/cancel/${op.webhookId}`);
      } catch (err) {
        console.warn(`Failed to cancel webhook ${op.webhookId} on server:`, err);
      }
    }

    op.status = "cancelled";
    op.error = "Cancelled by user";
    op.updatedAt = new Date().toISOString();
    this._moveToCompleted(op.id);
    this._notify();

    emitToasterMsg('info', 'Operation Cancelled', `"${op.label}" was cancelled.`);
  }

  /**
   * Stop polling and clean up.
   */
  destroy() {
    if (this.pollingTimer) {
      clearInterval(this.pollingTimer);
      this.pollingTimer = null;
    }
    this.listeners = [];
  }

  /**
   * Re-attach callbacks to an in-progress operation after a page refresh.
   * @param {string} webhookId
   * @param {Object} callbacks
   * @param {Function} [callbacks.onComplete]
   * @param {Function} [callbacks.onError]
   * @param {Function} [callbacks.pollStatusFn]
   */
  reattachCallbacks(webhookId, { onComplete, onError, pollStatusFn } = {}) {
    const op = this.queue.find((o) => o.webhookId === webhookId);
    if (!op) return false;

    if (onComplete) op.onComplete = onComplete;
    if (onError) op.onError = onError;
    if (pollStatusFn) op.pollStatusFn = pollStatusFn;
    return true;
  }

  /**
   * Respond to a waiting-for-input operation (confirm or deny).
   * @param {string} operationId
   * @param {boolean} confirmed
   */
  async respondToPrompt(operationId, confirmed) {
    const op = this.queue.find((o) => o.id === operationId);
    if (!op || !op.webhookId) return;

    try {
      await axiosHttp.post(`api/Webhook/${op.webhookId}/respond`, { Confirmed: confirmed });
      op.status = "processing";
      op.prompt = null;
      op.updatedAt = new Date().toISOString();
      this._notify();

      if (this.pollingTimer) {
        clearInterval(this.pollingTimer);
        this.pollingTimer = null;
      }
      this._startPolling();
    } catch (error) {
      this._failOperation(operationId, error?.response?.data?.message || "Failed to send response.");
    }
  }
}

const webhookQueueService = new WebhookQueueService();
export default webhookQueueService;